import React, { useState, useCallback, useEffect } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Cropper from 'react-easy-crop'
import { Area } from 'react-easy-crop/types'

import { getCroppedImg } from '../../common/cropImageHelper'

import { CropContainer, Controls, SliderControl } from './styles'

interface ImageCropModalProps {
  show: boolean
  imageSrc: string
  onHide: () => void
  onCropComplete: (file: File, previewUrl: string) => void
  aspectRatio?: number
  cropShape?: 'rect' | 'round'
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  show,
  imageSrc,
  onHide,
  onCropComplete,
  aspectRatio = 1,
  cropShape = 'rect',
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [processing, setProcessing] = useState(false)
  const [cropperKey, setCropperKey] = useState(0)

  const onCropCompleteInternal = useCallback(
    (_croppedArea: Area, pixels: Area) => {
      setCroppedAreaPixels(pixels)
    },
    []
  )

  // Reset state and force Cropper re-mount when modal opens
  useEffect(() => {
    if (show) {
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setRotation(0)
      setCroppedAreaPixels(null)
      setProcessing(false)
      setCropperKey(prev => prev + 1)
    }
  }, [show])

  const handleClose = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    setCroppedAreaPixels(null)
    setProcessing(false)
    onHide()
  }

  const handleSave = async () => {
    if (!croppedAreaPixels) return

    setProcessing(true)
    const { file, url } = await getCroppedImg(
      imageSrc,
      croppedAreaPixels,
      rotation,
      'avatar.jpg'
    )
    setProcessing(false)
    onCropComplete(file, url)
    handleClose()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Photo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CropContainer>
          <Cropper
            key={cropperKey}
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropCompleteInternal}
            cropShape={cropShape}
          />
        </CropContainer>
        <Controls>
          <SliderControl>
            <label htmlFor="zoom">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              id="zoom"
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
            />
          </SliderControl>
          <SliderControl>
            <label htmlFor="rotation">Rotation</label>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              id="rotation"
              value={rotation}
              onChange={e => setRotation(Number(e.target.value))}
            />
          </SliderControl>
        </Controls>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={processing}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={processing}>
          {processing ? 'Processing...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
